import { clearDraft, readDraft, writeDraft, moveDraft } from "../../lib/drafts";
import { DraftNotice } from "../../components/common/DraftNotice";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/common/Button";
import { Modal } from "../../components/common/Modal";
import {
	inspectorData,
	taskInspectorPanel,
} from "../../components/dock/kinds/inspector/data";
import { useDockStore, type DockPanel } from "../../stores/dock";
import { toast } from "../../stores/toast";
import { useRetargetTaskUrl } from "./taskUrl";
import {
	addTaskProgress,
	deleteTask,
	fetchTask,
	fetchProjectsLite,
	fetchAgentRoster,
	fetchFocusTaskTotals,
	handOverTask,
	reassignTask,
	renameTask,
	submitTaskFeedback,
	updateTask,
	updateTaskStatus,
	type TaskDetail,
	type ProjectLite,
	type RosterAgent,
} from "../../lib/api";
import {
	TASKS_CHANGED,
	TASK_MODES,
	TASK_PRIORITIES,
	TASK_STATUSES,
	statusLabel,
	taskSummary,
	taskBrief,
	emitTasksChanged,
	formatFocusEffort,
} from "./taskModel";
import styles from "./TasksPage.module.css";
import { useRegisterCommands } from "../../hooks/useRegisterCommands";

type TaskDraft = Partial<Record<"title" | "brief" | "domain" | "note" | "feedback", string>>;
const taskDraftKey = (project: string, slug: string) => `task:${JSON.stringify([project, slug])}`;
function readTaskDraft(key: string): TaskDraft {
  const value = readDraft<Record<string, unknown>>(key);
  return Object.fromEntries(["title", "brief", "domain", "note", "feedback"].filter(field => typeof value?.[field] === "string").map(field => [field, value![field]]));
}

/** Self-fetching, project-scoped editor shared by Tasks and Projects. */
export function TaskInspector({ panel }: { panel: DockPanel }) {
	const props = inspectorData(panel).props;
	const slug = String(props.slug ?? "");
	const project = String(props.project ?? "personal");
	const { t } = useTranslation();
	const draftKey = taskDraftKey(project, slug);
	const [hasDraft, setHasDraft] = useState(false);
	const [draftReadyKey, setDraftReadyKey] = useState("");
	const persistDraft = (draft: TaskDraft) => {
		if (Object.keys(draft).length) writeDraft(draftKey, draft); else clearDraft(draftKey);
		setHasDraft(Boolean(Object.keys(draft).length));
	};
	const editDraft = (field: keyof TaskDraft, value: string, saved = "") => {
		const draft = readTaskDraft(draftKey);
		if (value === saved) delete draft[field]; else draft[field] = value;
		persistDraft(draft);
	};
	const [detail, setDetail] = useState<TaskDetail | null>(null);
	const [projects, setProjects] = useState<ProjectLite[]>([]);
	const [roster, setRoster] = useState<RosterAgent[]>([]);
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);
	const lock = useRef(false);
	const request = useRef(0);
	const [title, setTitle] = useState("");
	const [brief, setBrief] = useState("");
	const [domain, setDomain] = useState("");
	const [note, setNote] = useState("");
	const [feedback, setFeedback] = useState("");
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [focus, setFocus] = useState<{
		sessions: number;
		seconds: number;
		abandoned: number;
	} | null>(null);
	const close = useDockStore((s) => s.closePanel);
	const replace = useDockStore((s) => s.replacePanel);
	// Rename, move and delete change this task's identity; the page that keeps
	// it in the URL (?task=project/slug) must follow, with replace.
	const retargetTaskUrl = useRetargetTaskUrl();
	const load = useCallback(async () => {
		const seq = ++request.current;
		try {
			const next = await fetchTask(slug, project);
			if (seq !== request.current) return;
			setDetail(next);
			setError("");
		} catch (err) {
			if (seq === request.current)
				setError(err instanceof Error ? err.message : "Could not load task.");
		}
	}, [slug, project]);
	useEffect(() => {
		setDetail(null);
		void load();
		const changed = () => {
			if (!lock.current) void load();
		};
		window.addEventListener(TASKS_CHANGED, changed);
		return () => {
			request.current++;
			window.removeEventListener(TASKS_CHANGED, changed);
		};
	}, [load]);
	useEffect(() => {
		fetchProjectsLite()
			.then(setProjects)
			.catch(() =>
				setError("Could not load projects. Reload the panel to try again."),
			);
		fetchAgentRoster()
			.then((r) => setRoster(r.filter((a) => a.status === "active")))
			.catch(() => {});
	}, []);
	useEffect(() => {
		let live = true;
		setFocus(null);
		fetchFocusTaskTotals(slug, project)
			.then((r) => {
				if (live) setFocus({ ...r, abandoned: r.abandoned ?? 0 });
			})
			.catch(() => {});
		return () => {
			live = false;
		};
	}, [slug, project]);
	// Refreshes of status/progress must not erase a description the user is typing.
	const savedBrief = detail ? taskBrief(detail) : "";
	const savedTitle = String(detail?.metadata.title ?? "");
	const savedDomain = String(detail?.metadata.domain ?? "general");
    useEffect(() => {
        if (!detail) return;
        const draft = readTaskDraft(draftKey);
        for (const [field, value] of Object.entries({ title: savedTitle, brief: savedBrief, domain: savedDomain })) {
            if (draft[field as keyof TaskDraft] === value) delete draft[field as keyof TaskDraft];
        }
        persistDraft(draft);
        setTitle(draft.title ?? savedTitle); setBrief(draft.brief ?? savedBrief); setDomain(draft.domain ?? savedDomain);
        setNote(draft.note ?? ""); setFeedback(draft.feedback ?? "");
        setDraftReadyKey(draftKey);
    }, [detail, draftKey]);
    const discardDraft = () => {
        clearDraft(draftKey); setHasDraft(false);
        setTitle(savedTitle); setBrief(savedBrief); setDomain(savedDomain); setNote(""); setFeedback("");
    };
	const mutate = async (
		work: () => Promise<unknown>,
		message: string,
		identityChange = false,
	) => {
		if (lock.current) return;
		lock.current = true;
		setBusy(true);
		setError("");
		try {
			await work();
			emitTasksChanged();
			toast.success(message);
			if (!identityChange) await load();
		} catch (err) {
			setError(
				`${err instanceof Error ? err.message : "Could not save task"}. Your change was not confirmed; try again.`,
			);
		} finally {
			lock.current = false;
			setBusy(false);
		}
	};
	useRegisterCommands(
		detail
			? [
					{
						id: "task:start",
						title: `Start: ${savedTitle}`,
						group: "Tasks",
						icon: "▶",
						run: () => {
							void mutate(
								() => updateTaskStatus(slug, "in_progress", project),
								"Task started",
							);
						},
					},
					{
						id: "task:review",
						title: `Submit for review: ${savedTitle}`,
						group: "Tasks",
						icon: "◎",
						run: () => {
							void mutate(
								() => updateTaskStatus(slug, "review", project),
								"Task ready for review",
							);
						},
					},
					{
						id: "task:done",
						title: `Mark done: ${savedTitle}`,
						group: "Tasks",
						icon: "✓",
						run: () => {
							void mutate(
								() => updateTaskStatus(slug, "done", project),
								"Task completed",
							);
						},
					},
					{
						id: "task:block",
						title: `Mark blocked: ${savedTitle}`,
						group: "Tasks",
						icon: "!",
						run: () => {
							void mutate(
								() => updateTaskStatus(slug, "blocked", project),
								"Task blocked",
							);
						},
					},
				]
			: [],
		[slug, project, savedTitle, detail?.metadata.status],
	);
	if (!detail || draftReadyKey !== draftKey)
		return (
			<div className={styles.inspector}>
				{error ? (
					<>
						<p role="alert">{error}</p>
						<Button onClick={() => void load()}>Retry</Button>
					</>
				) : (
					<p role="status">Loading task…</p>
				)}
			</div>
		);
	const task = taskSummary(detail, project);
	const rename = () => {
		if (!title.trim()) {
			setTitle(task.title); editDraft("title", task.title, task.title);
			return;
		}
		if (title.trim() === task.title) return;
		void mutate(
			async () => {
				const result = await renameTask(slug, title.trim(), project);
				editDraft("title", "");
				moveDraft(draftKey, taskDraftKey(result.project ?? project, result.slug));
				retargetTaskUrl(
					{ project, slug },
					{ project: result.project ?? project, slug: result.slug },
					() =>
						replace(
							panel.id,
							taskInspectorPanel({ ...result, title: title.trim() }),
						),
				);
				if (result.slug === slug) await load();
			},
			"Task renamed",
			true,
		);
	};
	return (
		<div className={styles.inspector}>
			<h2>{task.title}</h2>
			{error && (
				<p role="alert" className={styles.error}>
					{error}
				</p>
			)}
			<p className={styles.hint} aria-live="polite">
				{busy ? "Saving…" : "Changes save when you leave a field."}
			</p>
			{hasDraft && !busy && <DraftNotice draftKey={draftKey} onDiscard={discardDraft} />}
			<fieldset disabled={busy} className={styles.fieldset}>
				<label className={styles.field}>
					Title
					<input
						aria-label="Task title"
						value={title}
						onChange={(e) => { setTitle(e.target.value); editDraft("title", e.target.value, savedTitle); }}
						onBlur={(event) => { if (!(event.relatedTarget instanceof Element && event.relatedTarget.closest("[data-discard-draft]"))) rename(); }}
						onKeyDown={(e) => {
							if (e.key === "Enter") e.currentTarget.blur();
							if (e.key === "Escape") { setTitle(task.title); editDraft("title", task.title, task.title); }
						}}
					/>
				</label>
				<div className={styles.fieldRow}>
					<label className={styles.field}>
						Project
						<select
							value={task.project}
							onChange={(e) => {
								const target = e.target.value;
								void mutate(
									async () => {
										const result = await reassignTask(slug, target, project);
										moveDraft(draftKey, taskDraftKey(result.project, result.slug));
										retargetTaskUrl({ project, slug }, result, () =>
											replace(
												panel.id,
												taskInspectorPanel({ ...result, title: task.title }),
											),
										);
									},
									"Task moved to project",
									true,
								);
							}}
						>
							<option value="personal">Personal</option>
							{task.project !== "personal" &&
								!projects.some((p) => p.slug === task.project) && (
									<option value={task.project}>{task.project}</option>
								)}
							{projects
								.filter((p) => p.slug !== "personal")
								.map((p) => (
									<option key={p.slug} value={p.slug}>
										{p.title}
									</option>
								))}
						</select>
					</label>
					<label className={styles.field}>
						Status
						<select
							value={task.status}
							onChange={(e) =>
								void mutate(
									() => updateTaskStatus(slug, e.target.value, project),
									"Status updated",
								)
							}
						>
							{TASK_STATUSES.map((s) => (
								<option key={s} value={s}>
									{statusLabel(s)}
								</option>
							))}
						</select>
					</label>
					<label className={styles.field}>
						Assigned to
						<select
							value={task.assigned_agent || ""}
							onChange={(e) =>
								void mutate(
									() => handOverTask(slug, e.target.value, "", project),
									"Assignment saved",
								)
							}
						>
							<option value="" disabled>
								Unassigned
							</option>
							{task.assigned_agent &&
								!roster.some((a) => a.key === task.assigned_agent) && (
									<option value={task.assigned_agent}>
										{task.assigned_agent}
									</option>
								)}
							{roster.map((a) => (
								<option key={a.key} value={a.key}>
									{a.name}
								</option>
							))}
						</select>
					</label>
					<label className={styles.field}>
						Priority
						<select
							value={task.priority}
							onChange={(e) =>
								void mutate(
									() => updateTask(slug, { priority: e.target.value }, project),
									"Priority saved",
								)
							}
						>
							{TASK_PRIORITIES.map((p) => (
								<option key={p}>{p}</option>
							))}
						</select>
					</label>
					<label className={styles.field}>
						Due date
						<input
							type="date"
							value={task.due}
							onChange={(e) =>
								void mutate(
									() => updateTask(slug, { due: e.target.value }, project),
									"Due date saved",
								)
							}
						/>
					</label>
					<label className={styles.field}>
						Mode
						<select
							value={task.mode}
							onChange={(e) =>
								void mutate(
									() => updateTask(slug, { mode: e.target.value }, project),
									"Mode saved",
								)
							}
						>
							{Object.entries(TASK_MODES).map(([id, label]) => (
								<option key={id} value={id}>
									{label}
								</option>
							))}
						</select>
					</label>
					<label className={styles.field}>
						Domain
						<input
							value={domain}
							onChange={(e) => { setDomain(e.target.value); editDraft("domain", e.target.value, savedDomain); }}
							onBlur={(event) => {
								if (event.relatedTarget instanceof Element && event.relatedTarget.closest("[data-discard-draft]")) return;
								if (domain.trim() && domain !== task.domain)
									void mutate(
										() => updateTask(slug, { domain: domain.trim() }, project),
										"Domain saved",
									);
							}}
						/>
					</label>
				</div>
				<label className={styles.field}>
					Description
					<textarea
						aria-label={t("tasks.details")}
						rows={6}
						value={brief}
						onChange={(e) => { setBrief(e.target.value); editDraft("brief", e.target.value, savedBrief); }}
						onBlur={(event) => {
								if (event.relatedTarget instanceof Element && event.relatedTarget.closest("[data-discard-draft]")) return;
							if (brief !== savedBrief)
								void mutate(
									() => updateTask(slug, { description: brief }, project),
									"Description saved",
								);
						}}
					/>
				</label>
				<div className={styles.actions}>
					<Link to={`/focus?task=${encodeURIComponent(slug)}&project=${encodeURIComponent(project)}`}>
						Focus on this task
					</Link>
					<span className={styles.hint}>{formatFocusEffort(focus, t)}</span>
				</div>
				<label className={styles.field}>
					Progress note
					<textarea
						aria-label="Progress note"
						value={note}
						onChange={(e) => { setNote(e.target.value); editDraft("note", e.target.value, ""); }}
						rows={2}
					/>
				</label>
				<Button
					disabled={!note.trim()}
					onClick={() =>
						void mutate(async () => {
							await addTaskProgress(slug, note.trim(), project);
							setNote(""); editDraft("note", "");
						}, "Progress added")
					}
				>
					Add progress
				</Button>
				<details className={styles.history}>
					<summary>Progress, results and task history</summary>
					<pre>{detail.content}</pre>
				</details>
				{task.status === "done" && (
					<div className={styles.form}>
						<label className={styles.field}>
							Feedback
							<input
								aria-label="Feedback"
								value={feedback}
								onChange={(e) => { setFeedback(e.target.value); editDraft("feedback", e.target.value, ""); }}
							/>
						</label>
						<div className={styles.actions}>
							{(
								[
									["good", "Good"],
									["needs_improvement", "Needs work"],
									["redo", "Redo"],
								] as const
							).map(([rating, label]) => (
								<Button
									key={rating}
									variant="secondary"
									onClick={() =>
										void mutate(async () => {
											await submitTaskFeedback(slug, rating, feedback, project);
											setFeedback(""); editDraft("feedback", "");
										}, "Feedback saved")
									}
								>
									{label}
								</Button>
							))}
						</div>
					</div>
				)}
				<Button variant="danger" onClick={() => setConfirmDelete(true)}>
					Delete task
				</Button>
			</fieldset>
			<Modal
				open={confirmDelete}
				onClose={() => !busy && setConfirmDelete(false)}
				title="Delete task?"
				dismissOnBackdrop={false}
				footer={
					<>
						<Button
							variant="secondary"
							disabled={busy}
							onClick={() => setConfirmDelete(false)}
						>
							Cancel
						</Button>
						<Button
							variant="danger"
							disabled={busy}
							onClick={() =>
								void mutate(
									async () => {
										await deleteTask(slug, project);
										clearDraft(draftKey);
										// Replace, so Back cannot land on the deleted task.
										retargetTaskUrl({ project, slug }, null, () => close(panel.id));
									},
									"Task deleted",
									true,
								)
							}
						>
							Delete task
						</Button>
					</>
				}
			>
				<p>
					Delete “{task.title}” from {task.project}? This cannot be undone.
				</p>
			</Modal>
		</div>
	);
}
